
import {
  computed, defineComponent, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDropZone } from '@vueuse/core';
// eslint-disable-next-line import/no-cycle
import { useMessagesStore } from '../store/messagesStore';
import MessageItem from '../components/MessageItem.vue';
import MessageMockItem from '@/modules/messages/components/MessageMockItem.vue';
import AppMessageInput from '@/components/UI/AppMessageInput.vue';
// eslint-disable-next-line import/no-cycle
import WebsocketService from '@/services/WebsocketService';
// eslint-disable-next-line
import { CONTACT_STATUS, MESSAGE_CATEGORY, NAVIGATION_PAGES, UPLOAD_PLACES } from '@/types/enums';
import {
  IConversationItemGroupList, IMockMessage, IImageCrop,
} from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import AppSkeletonMessageMedia from '@/components/skeletons/AppSkeletonMessageMedia.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import SkeletonService from '@/services/SkeletonService';
import AppSkeletonDialog from '@/components/skeletons/AppSkeletonDialog.vue';
// eslint-disable-next-line import/no-cycle
import NativeService from '@/services/NativeService';
import isPhoto from '@/helpers/fileDetector';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '@/modules/drive/store/driveStore';
// eslint-disable-next-line
import UploadService from '@/services/UploadService';
import AppNoMessage from '@/components/AppNoMessage.vue';
import UseUpload from '@/composables/UseUpload';
import AppDropZone from '@/components/AppDropZone.vue';

export default defineComponent({
  components: {
    MessageItem,
    MessageMockItem,
    AppMessageInput,
    AppSkeletonMessageMedia,
    AppSkeletonDialog,
    AppNoMessage,
    AppDropZone,
  },
  props: {
    loaderText: { type: String, default: 'loading messages' },
    isGroup: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
  },
  setup(props) {
    // TODO to reuse normally function in MessagesId and MessagesGroupID
    /** The chat container ".dialog__list" is rotated vertically to make scrolling smoother */
    /** Each "dialog__item" message is rotated to its original orientation so that the text is displayed correctly */
    /** Note that this also reverses the orientation: top becomes bottom, and vice versa */

    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();

    const fullscreenLoading = ref(false);

    const driveStore = useDriveStore();
    const { onUploadUpdate } = UseUpload();

    const { currentDialogsSkeletons } = SkeletonService.getSkeletonInfo();

    const dataLoading = ref<boolean>(true);
    const scrollItem = ref<HTMLDivElement | null>(null);
    const messagesLoading = ref<boolean>(false);

    let navigationX = 0;

    const groupId = computed(() => route.params.id as string);

    const mockMessages = computed(() => messagesStore.mockMessages.find((list) => list.key === groupId.value)?.value || []);

    const hideSkeletons = computed(() => !!route.query.hideSkeletons);
    const showGoBottom = ref(false);
    const mediaLoadedKey = ref(0);
    const mediaToCrop = ref({} as IImageCrop);
    const mediaInputRef = ref();
    const mediaUploading = ref(false);
    const dialogId = ref(route.params.id);

    const messages = computed(() => messagesStore.currentConversation?.messages
      .filter((v, i, a) => a.findIndex((v2) => (v2.id === v.id)) === i).reverse() || []);
    const skeletons = computed(() => {
      if (messagesStore.currentConversation?.info.id) {
        const val = currentDialogsSkeletons[messagesStore.currentConversation.info.id];
        return val || 0;
      }
      return 0;
    });

    function sendMessage(message: string) {
      if (!message) {
        return;
      }
      WebsocketService.sendMessage(groupId.value, message, true);
    }

    function resendMessage(mockMsg: IMockMessage) {
      messagesStore.removeMockMessage(groupId.value, mockMsg.uuid);
      sendMessage(mockMsg.text);
    }

    function scrollToBottom(behaviorSmooth?: boolean): void {
      setTimeout(() => {
        if (scrollItem.value) {
          scrollItem.value.scrollTo({
            top: 0,
            behavior: behaviorSmooth ? 'smooth' : 'auto',
          });
        }
      }, 100);
    }

    function scrollToFirstUnread(): void {
      if (messagesStore.currentConversation?.messages?.length) {
        const idx = messagesStore.currentConversation.messages.findIndex(
          (msg) => msg.owner.accountId !== mainStore.user.accountId && !msg.read,
        );
        const msg = document.querySelectorAll('.msg-item')[idx];
        if (msg) {
          msg.scrollIntoView({ block: 'center', inline: 'nearest' });
        } else {
          scrollToBottom();
        }
      }
    }

    function readMessages(): void {
      messagesStore.currentConversation!.messages.forEach((msg) => {
        if (msg.owner.accountId !== mainStore.user.accountId && !msg.read) {
          WebsocketService.readMessage(messagesStore.currentConversation!.info.id, msg.content, msg.id, msg.owner.accountId);
          // eslint-disable-next-line no-param-reassign
          msg.read = true;
        }
      });
      const currentConv = messagesStore.conversations.find((conv) => conv.conversationId === messagesStore.currentConversation?.conversationId);
      if (currentConv) {
        currentConv.unreadCount = 0;
      }
    }

    function setShowDate(): void {
      if (!messagesStore.currentConversation?.messages?.length) {
        return;
      }

      let previousDate = messagesStore.currentConversation.messages[messagesStore.currentConversation.messages?.length - 1]?.created;

      if (!previousDate) {
        return;
      }

      function isDaysEqual(prevDate: Date, msgDate: Date): boolean {
        return prevDate.getFullYear() === msgDate.getFullYear() && prevDate.getMonth() === msgDate.getMonth() && prevDate.getDay() === msgDate.getDay();
      }

      messagesStore.currentConversation.messages.forEach((msg) => {
        const prevDate = new Date(previousDate);
        const msgDate = new Date(msg.created);
        if (!isDaysEqual(prevDate, msgDate)) {
          // eslint-disable-next-line no-param-reassign
          msg.showDate = true;
        } else {
          // eslint-disable-next-line no-param-reassign
          msg.showDate = false;
        }
        previousDate = msg.created;
      });

      messagesStore.currentConversation!.messages[0].showDate = true;
    }

    /**
     * Sets the 'showName' flag for each message in the current conversation
     * Depending on this, the name of the message sender is displayed
     */
    function setShowName(): void {
      if (!messagesStore.currentConversation?.messages?.length) {
        return;
      }

      let previousOwnerId = messagesStore.currentConversation.messages[messagesStore.currentConversation.messages?.length - 1]?.owner?.accountId;

      messagesStore.currentConversation.messages.forEach((msg) => {
        const isMy = msg.owner?.accountId === mainStore.user.accountId;
        if (!isMy && (msg.owner?.accountId !== previousOwnerId || msg.showDate)) {
          // eslint-disable-next-line no-param-reassign
          msg.showName = true;
        } else {
          // eslint-disable-next-line no-param-reassign
          msg.showName = false;
        }
        previousOwnerId = msg.owner?.accountId;
      });
    }

    function updateMessageFlags() {
      setShowDate();
      setShowName();
    }

    function onScroll(): void {
      if (scrollItem.value) {
        const { scrollTop } = scrollItem.value;
        showGoBottom.value = scrollTop > 500;
      }
      if (!messagesStore.currentConversation?.previousMessageTimestamp) {
        return;
      }
      if (scrollItem.value) {
        const isTop = (scrollItem.value.scrollTop + scrollItem.value.clientHeight) >= scrollItem.value.scrollHeight - 1000;

        if (isTop && !messagesLoading.value) {
          messagesLoading.value = true;
          messagesStore.fetchGroupConversationById(groupId.value, undefined, true)
            .then(() => {
              updateMessageFlags();
              messagesLoading.value = false;
            });
        }
      }
    }

    function formatDate(date: string): string {
      const today = new Date();
      const currentDate = new Date(date);

      const isToday = (
        currentDate.getDate() === today.getDate()
        && currentDate.getMonth() === today.getMonth()
        && currentDate.getFullYear() === today.getFullYear()
      );

      const isThisYear = currentDate.getFullYear() === today.getFullYear();

      if (isToday) {
        return 'Today';
      }
      if (isThisYear) {
        return currentDate.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'long',
        });
      }
      return currentDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    watch(() => messagesStore.currentConversation?.messages?.length, (newLength, oldLength) => {
      if (newLength && !messagesLoading.value) {
        scrollToBottom();
        readMessages();
        updateMessageFlags();
      }
    });

    function formatTime(time: string) {
      return new Date(time).toLocaleString('en-US', { hour: 'numeric', hour12: false, minute: 'numeric' });
    }

    function elementDraged(event: MouseEvent | TouchEvent) {
      if ((event as MouseEvent)?.clientX) {
        navigationX = (event as MouseEvent)?.clientX;
        return;
      }
      if ((event as TouchEvent)?.changedTouches?.length) {
        navigationX = (event as TouchEvent)?.changedTouches[0]?.clientX;
      }
    }

    function mouseMove(event: MouseEvent) {
      let movementX = navigationX - event.clientX;

      if (movementX < 15) {
        movementX = 0;
      }
      if (movementX > 75) {
        movementX = 75;
      }

      if (scrollItem.value) {
        scrollItem.value.scrollLeft = movementX;
      }
    }

    function elementDroped(event: MouseEvent | TouchEvent) {
      if ((event as MouseEvent)?.clientX) {
        navigationX = 0;
      }
      if (scrollItem.value) {
        scrollItem.value.scrollLeft = 0;
      }
    }

    function touchMove(event: TouchEvent) {
      let movementX = navigationX - event.changedTouches[0].clientX;

      if (movementX < 15) {
        movementX = 0;
      }
      if (movementX > 75) {
        movementX = 75;
      }

      if (scrollItem.value) {
        scrollItem.value.scrollLeft = movementX;
      }
    }

    function checkQuery() {
      const shareSettings = route.query.shareSettings ? JSON.parse(route.query.shareSettings as string) : null;
      if (route.query.sendCase) {
        WebsocketService.sendCase(groupId.value, route.query.sendCase as string, '', true, shareSettings);
        router.replace({});
      } else if (route.query.sendFile) {
        WebsocketService.sendMedia(groupId.value, route.query.sendFile as string, true, shareSettings);
        router.replace({});
      } else if (route.query.sendFolder) {
        WebsocketService.sendFolder(groupId.value, route.query.sendFolder as string, true);
        router.replace({});
      }
    }

    function onStartUpload() {
      scrollToBottom();
    }

    function blurInput() {
      const input = document.getElementById('input-field');
      if (input) {
        input.blur();
      }
    }
    function addMedia(e: any) {
      const img: File = (e.target as HTMLInputElement).files![0];
      mediaToCrop.value = {
        url: URL.createObjectURL(img),
        fileName: img.name,
        fileType: img.type,
      };
      mediaLoadedKey.value += 1;

      mediaInputRef.value!.value = '';
    }

    function splitMedia(mediaList: File[]) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }
    function onDrop(mediaList: File[] | null) {
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);

        const fileUploads = UploadService.toFileUploads(fileList as unknown as FileList);
        const photoUploads = UploadService.toFileUploads(photoList as unknown as FileList);

        const convId = messagesStore.currentConversation!.info.id;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
            .then((response) => {
              response.forEach((res) => {
                WebsocketService.sendMedia(
                  convId,
                  res.id,
                  props.isGroup,
                );
              });
            })
            .finally(() => {
              mediaUploading.value = false;
            });
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
            .then((response) => {
              response.forEach((res) => {
                WebsocketService.sendMedia(
                  convId,
                  res.id,
                  props.isGroup,
                );
              });
            })
            .finally(() => {
              mediaUploading.value = false;
            });
        }
      }
    }
    const dropZoneRef = ref<HTMLElement>();

    const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

    function fetchConv() {
      const currentConv = messagesStore.conversations.find((conv) => (conv as IConversationItemGroupList)?.group?.id === groupId.value);

      messagesStore.fetchGroupConversationById(groupId.value, 25 + (currentConv?.unreadCount || 0))
        .finally(() => {
          updateMessageFlags();
          scrollToFirstUnread();
          readMessages();
          // subscribeOnStore();
          checkQuery();
          WebsocketService.syncChat();
          setTimeout(() => {
            dataLoading.value = false;
            fullscreenLoading.value = false;
          }, 100);
        });
    }

    function reverseScroll(event) {
      event.preventDefault();

      const scrollSpeed = 2; // adjust as desired

      if (scrollItem.value) {
        scrollItem!.value.scrollTop += (event.deltaY * -scrollSpeed);
      }
    }

    watch(() => route.params.id, (value, oldValue) => {
      if (route.params.id && route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID) {
        NativeService.awakeCallback = fetchConv;
        NativeService.toggleDialogNotifications(oldValue as string, MESSAGE_CATEGORY.GROUP_MESSAGING, 'on');
        NativeService.toggleDialogNotifications(value as string, MESSAGE_CATEGORY.GROUP_MESSAGING, 'off');
        dialogId.value = value;
        fetchConv();
        driveStore.resetCurrentCase();
        dataLoading.value = true;
      }
    });

    // watch(() => messagesStore.toBeSync, (newVal, oldVal) => {
    //   console.log('TO_BE_SYNC', newVal);
    //   if (newVal) {
    //     fullscreenLoading.value = true;
    //     NativeService.awakeCallback = fetchConv;
    //     fetchConv();
    //     messagesStore.toBeSync = false;
    //   }
    // }, { immediate: true });

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      NativeService.awakeCallback = fetchConv;
      NativeService.toggleDialogNotifications(route.params.id as string, MESSAGE_CATEGORY.GROUP_MESSAGING, 'off');
      fetchConv();
    });

    onUnmounted(() => {
      messagesStore.currentConversation = null;
      NativeService.awakeCallback = null;
      NativeService.toggleDialogNotifications(dialogId.value as string, MESSAGE_CATEGORY.GROUP_MESSAGING, 'on');
    });

    return {
      fullscreenLoading,
      messages,
      skeletons,
      dataLoading,
      messagesStore,
      scrollItem,
      messagesLoading,
      currentDialogsSkeletons,
      hideSkeletons,
      showGoBottom,
      mockMessages,
      mainStore,
      onStartUpload,
      sendMessage,
      formatDate,
      onScroll,
      elementDraged,
      mouseMove,
      touchMove,
      elementDroped,
      formatTime,
      scrollToBottom,
      blurInput,
      resendMessage,
      CONTACT_STATUS,
      isOverDropZone,
      onDrop,
      splitMedia,
      dropZoneRef,
      addMedia,
      mediaToCrop,
      mediaInputRef,
      mediaUploading,
      reverseScroll,
    };
  },
});
